import React from 'react';
import { Progress, Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { CheckOutlined } from '@ant-design/icons';
import ProductNameWithDimensions from '../../../components/products/ProductNameWithDimensions';
import type { OrderDto } from '../../../dto/order';
import type { OrderProduct } from '../../../types/order';
import type { OrderStockLine, OrderStockStatus } from '../api/orderStockStatusApi';
import type { OrderWarehouseLine } from '../../../types/orderWarehouseSummary';
import { formatCurrencyLocalized } from '../../../utils/currency';
import { formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { formatUnit } from '../../../utils/units';
import { formatCommercialPriceUnit } from '../commercialPriceUnit';
import { getOrderProductDisplayDimensions, getOrderProductDisplayName, type OrderProductDisplayInput } from '../orderProductDisplay';
import { getTotalsByCurrency, type OrderProductLineDraft } from '../orderProductLineTotals';
import OrderQuantityPreview from '../components/OrderQuantityPreview';
import { HiddenValue } from './OrderProcessParts';

/** „30 m²” — quantity with the Polish unit label. */
export function useQuantityFormatter() {
  const { i18n } = useTranslation();
  return (value: number | null | undefined, unit: string | null | undefined) =>
    value == null ? '—' : `${formatLocalizedQuantity(value, i18n.language)} ${formatUnit(unit || 'pcs', i18n.language)}`;
}

/** Stock state of one line (stock-status): Dostępne / Brakuje X / Wydane. */
export function StockStateTag({ line }: { line: Pick<OrderStockLine, 'state' | 'missing' | 'unit'> }) {
  const { t } = useTranslation(['orders']);
  const quantity = useQuantityFormatter();
  if (line.state === 'ISSUED') return <Tag color="blue">{t('orders:process.stock.issued')}</Tag>;
  if (line.state === 'AVAILABLE') return <Tag color="green">{t('orders:process.stock.available')}</Tag>;
  if (line.state === 'MISSING') {
    return <Tag color="orange">{t('orders:process.stock.missing', { quantity: quantity(line.missing, line.unit) })}</Tag>;
  }
  return <Tag>{t('orders:process.stock.unknown')}</Tag>;
}

/**
 * Product name with its dimensions in a muted line underneath (pattern of the
 * stock table). `product` is the order line (or any DTO with the same
 * dimension fields); nothing is rendered under the name without dimensions.
 */
export function OrderLineNameWithDimensions({ name, product, testId }: {
  name: React.ReactNode;
  product?: OrderProductDisplayInput | null;
  testId: string;
}) {
  return <ProductNameWithDimensions name={name} dimensions={product} dimensionsTestId={testId} />;
}

/** Positions with quantity, sale price and value (makieta `linesTable()`); prices masked for Operations. */
export function OrderLinesSummaryTable({
  order,
  canViewSalePrices,
  extraColumn,
}: {
  order: OrderDto;
  canViewSalePrices: boolean;
  extraColumn?: { title: string; render: (product: OrderProduct) => React.ReactNode };
}) {
  const { t, i18n } = useTranslation(['orders']);
  const products = order.productsLoaded ? order.products : [];
  const money = (value: number | null | undefined, currency?: string) =>
    formatCurrencyLocalized(value ?? 0, currency || order.currency || 'PLN', i18n.language);
  const totals = Object.entries(getTotalsByCurrency(products as readonly OrderProductLineDraft[], order.currency || 'PLN'));
  return (
    <>
      <Table<OrderProduct>
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={products}
        data-testid="zk-lines-table"
        locale={{ emptyText: t('orders:process.lines.empty') }}
        columns={[
          { title: t('orders:process.lines.no'), key: 'no', width: 48, render: (_v, _r, index) => index + 1 },
          {
            title: t('orders:process.lines.product'),
            key: 'product',
            render: (_v, product) => (
              <OrderLineNameWithDimensions name={getOrderProductDisplayName(product, product.name)} product={product} testId="zk-lines-line-dimensions" />
            ),
          },
          {
            title: t('orders:process.lines.quantity'),
            key: 'quantity',
            align: 'right',
            render: (_v, product) => <OrderQuantityPreview line={product} />,
          },
          {
            title: t('orders:process.lines.salePrice'),
            key: 'price',
            align: 'right',
            render: (_v, product) => (canViewSalePrices
              ? <span className="zk-num">{money(product.unitPrice, product.currency)} / {formatCommercialPriceUnit('sale', product, i18n.language)}</span>
              : <HiddenValue reason="salePrices" />),
          },
          {
            title: t('orders:process.lines.value'),
            key: 'value',
            align: 'right',
            render: (_v, product) => (canViewSalePrices
              ? <span className="zk-num">{money(product.totalPrice, product.currency)}</span>
              : <HiddenValue reason="salePrices" />),
          },
          ...(extraColumn ? [{ title: extraColumn.title, key: 'extra', render: (_v: unknown, product: OrderProduct) => extraColumn.render(product) }] : []),
        ]}
      />
      <div className="zk-total" data-testid="zk-lines-total">
        <span>{t('orders:process.lines.totalNet')}</span>
        <strong>{canViewSalePrices
          ? (totals.length ? totals.map(([currency, amount]) => money(amount, currency)).join(' / ') : money(0))
          : <HiddenValue reason="salePrices" />}</strong>
      </div>
    </>
  );
}

/**
 * Stock per line (makieta `stockTable()`): Zamówiono, Wydano, Na stanie,
 * Dostępne / Brakuje (decision 2026-10-04 pkt 6 — no „Czeka dla wcześniejszych
 * ZK” column). „Wydano” (already issued by WZ) appears once any line has an
 * issue, so the row adds up: Zamówiono − Wydano = Na stanie + Brakuje.
 * „Na stanie” is absent when the backend sends no stock figure.
 * Dimensions come from the order line (`products`) under the product name.
 */
export function OrderStockTable({ stockStatus, products = [] }: { stockStatus: OrderStockStatus | null; products?: OrderProduct[] }) {
  const { t } = useTranslation(['orders']);
  const quantity = useQuantityFormatter();
  const lines = stockStatus?.lines ?? [];
  const productsById = new Map(products.map((product) => [product.id, product]));
  const showOnHand = lines.some((line) => line.onHand !== null);
  const showIssued = lines.some((line) => (line.issued ?? 0) > 0);
  return (
    <Table<OrderStockLine>
      size="small"
      rowKey="orderProductId"
      pagination={false}
      dataSource={lines}
      data-testid="zk-stock-table"
      loading={!stockStatus}
      locale={{ emptyText: t('orders:process.lines.empty') }}
      columns={[
        {
          title: t('orders:process.lines.product'),
          key: 'name',
          render: (_v, line) => {
            const dimensions = getOrderProductDisplayDimensions(productsById.get(line.orderProductId));
            return (
              <div className="zk-line-identity">
                <span>{line.name}</span>
                {dimensions && <small data-testid="zk-stock-line-dimensions">{dimensions}</small>}
              </div>
            );
          },
        },
        {
          title: t('orders:process.stock.ordered'),
          key: 'ordered',
          align: 'right',
          render: (_v, line) => <OrderQuantityPreview line={{ ...productsById.get(line.orderProductId), quantity: line.ordered, unit: line.unit }} />,
        },
        ...(showIssued ? [{
          title: t('orders:process.stock.issuedQuantity'),
          key: 'issued',
          align: 'right' as const,
          render: (_v: unknown, line: OrderStockLine) => <span className="zk-num">{quantity(line.issued, line.unit)}</span>,
        }] : []),
        ...(showOnHand ? [{
          title: t('orders:process.stock.onHand'),
          key: 'onHand',
          align: 'right' as const,
          render: (_v: unknown, line: OrderStockLine) => <span className="zk-num">{quantity(line.onHand, line.unit)}</span>,
        }] : []),
        { title: t('orders:process.stock.status'), key: 'state', render: (_v, line) => <StockStateTag line={line} /> },
      ]}
    />
  );
}

/** Share of the ordered quantity already issued by WZ, in percent (0 when nothing is ordered). */
export function issuePercent(line: Pick<OrderWarehouseLine, 'orderedQuantity' | 'issuedQuantity'>) {
  const issued = line.issuedQuantity ?? 0;
  return line.orderedQuantity > 0 ? Math.min(100, Math.round((issued / line.orderedQuantity) * 100)) : 0;
}

/**
 * Issue progress per line from the warehouse summary (quantities only); dimensions from the order line.
 * Next to the bar: the issued amount and the result — „całość”, or what is missing / issued over the order.
 */
export function OrderIssueProgress({ lines, products = [] }: { lines: OrderWarehouseLine[]; products?: OrderProduct[] }) {
  const { t } = useTranslation(['orders']);
  const quantity = useQuantityFormatter();
  const productsById = new Map(products.map((product) => [product.id, product]));
  return (
    <div className="zk-progress-list" data-testid="zk-issue-progress">
      {lines.map((line) => {
        const issued = line.issuedQuantity ?? 0;
        const difference = issued - line.orderedQuantity;
        const complete = Math.abs(difference) < 1e-9;
        const ordered = quantity(line.orderedQuantity, line.unit);
        const result = complete
          ? t('orders:process.delivered.issuedComplete')
          : t(difference < 0 ? 'orders:process.delivered.issuedMissing' : 'orders:process.delivered.issuedOver', {
            quantity: quantity(Math.abs(difference), line.unit),
            ordered,
          });
        return (
          <div className="zk-progress" key={line.id}>
            <OrderLineNameWithDimensions name={line.name} product={productsById.get(line.id)} testId="zk-issue-line-dimensions" />
            <Progress
              percent={issuePercent(line)}
              status={complete ? 'success' : 'normal'}
              strokeColor={complete ? undefined : 'var(--app-warning)'}
              showInfo={false}
              size="small"
              aria-label={line.name}
            />
            <div className="zk-progress__result">
              <span className="zk-num">{quantity(issued, line.unit)}</span>
              <span
                className={`zk-progress__status zk-progress__status--${complete ? 'complete' : 'short'}`}
                data-testid="zk-issue-result"
              >
                {complete && <CheckOutlined aria-hidden="true" />} {result}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
